import { useCallback, useEffect, useState } from "react";
import { http, CATEGORIES } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Filter } from "lucide-react";
import { toast } from "sonner";
import { JobForm } from "@/components/app/jobs/JobForm";
import { JobsTable } from "@/components/app/jobs/JobsTable";

const emptyForm = {
  title: "", category: "ev", customer_id: "", customer_name: "", address: "",
  date: new Date().toISOString().slice(0, 10), price: 0, employee_id: "", employee_name: "",
  employee_payment: 0, status: "planlandi", recurring: "yok", recurring_count: 1,
  notes: "", paid: false,
};

const toPayload = (form) => ({
  ...form,
  price: parseFloat(form.price) || 0,
  employee_payment: parseFloat(form.employee_payment) || 0,
  recurring_count: parseInt(form.recurring_count) || 1,
});

export default function Jobs() {
  const [jobs, setJobs] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [filterCat, setFilterCat] = useState("all");

  const load = useCallback(async () => {
    const [j, e, c] = await Promise.all([http.get("/jobs"), http.get("/employees"), http.get("/customers")]);
    setJobs(j.data); setEmployees(e.data); setCustomers(c.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const closeDialog = () => { setOpen(false); setForm(emptyForm); setEditingId(null); };

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await http.put(`/jobs/${editingId}`, toPayload(form));
        toast.success("İş güncellendi");
      } else {
        const { data } = await http.post("/jobs", toPayload(form));
        toast.success(`${data.created} iş oluşturuldu`);
      }
      closeDialog(); load();
    } catch { toast.error("Kayıt başarısız"); }
  };

  const del = async (id) => {
    if (!window.confirm("Silmek istediğinize emin misiniz?")) return;
    await http.delete(`/jobs/${id}`);
    toast.success("Silindi"); load();
  };

  const startEdit = (j) => { setEditingId(j.id); setForm({ ...j, recurring: "yok", recurring_count: 1 }); setOpen(true); };

  const markPaid = async (j) => {
    await http.put(`/jobs/${j.id}`, { paid: !j.paid, status: !j.paid ? "tamamlandi" : j.status });
    load();
  };

  const filtered = jobs.filter((j) => filterCat === "all" || j.category === filterCat);

  return (
    <div className="space-y-6" data-testid="jobs-page">
      <div className="flex flex-wrap items-center gap-3">
        <Select value={filterCat} onValueChange={setFilterCat}>
          <SelectTrigger className="w-56" data-testid="filter-category-select">
            <Filter className="h-4 w-4 mr-2" /><SelectValue placeholder="Kategori filtresi" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tüm Kategoriler</SelectItem>
            {CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="ml-auto text-sm text-slate-500">{filtered.length} iş</div>
        <Dialog open={open} onOpenChange={(v) => (v ? setOpen(true) : closeDialog())}>
          <DialogTrigger asChild>
            <Button className="bg-emerald-600 hover:bg-emerald-700" data-testid="add-job-button"><Plus className="h-4 w-4 mr-2" /> Yeni İş</Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>{editingId ? "İş Düzenle" : "Yeni İş Ekle"}</DialogTitle></DialogHeader>
            <JobForm form={form} setForm={setForm} employees={employees} customers={customers} editing={!!editingId} onSubmit={submit} />
          </DialogContent>
        </Dialog>
      </div>
      <JobsTable jobs={filtered} onPaid={markPaid} onEdit={startEdit} onDelete={del} />
    </div>
  );
}
